import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import styled from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { logoutAction } from '@root/features/auth/logout/atoms'
import { environmentAtom } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { CopyButton, LogoGlass } from '@root/shared/ui-kit'

import {
  refreshTerminalAccessAction,
  terminalAccessStateAtom,
} from '../atoms/reatom-terminal-access'

/** Often enough that being let in shows without a reload. */
const POLL_MS = 30_000

/**
 * What a signed-in account sees until it is let in.
 *
 * Nothing of the terminal: the place in line, and — for somebody whose
 * account has Telegram — that the bot will say when it opens and a link of
 * their own to share. It checks again on its own, so the moment an admin
 * lets them in the app replaces it without anybody reloading.
 */
export const WaitlistScreen: React.FC = () => {
  const { t } = useTranslation()
  const [state] = useAtom(terminalAccessStateAtom)
  const [environment] = useAtom(environmentAtom)
  const refresh = useAction(refreshTerminalAccessAction)
  // Straight out, no confirmation: nothing is lost by leaving a queue.
  const logout = useAction(logoutAction)

  useEffect(() => {
    const timer = setInterval(() => refresh(), POLL_MS)
    return () => clearInterval(timer)
  }, [refresh])

  const access = pipe(
    state,
    AsyncStateN.getOrElseW(() => undefined),
  )

  const shareLink =
    access?.tgId && environment.buybotUsername
      ? `https://t.me/${environment.buybotUsername}?start=w_${access.tgId}`
      : undefined

  return (
    <Root>
      <Card>
        <LogoGlass size={72} />
        <Brand>kol.trade</Brand>

        <Title>{t('access.waitlist.title')}</Title>

        {access?.position === undefined ? undefined : (
          <Position>
            <PositionLabel>{t('access.waitlist.position')}</PositionLabel>
            <PositionValue>#{access.position}</PositionValue>
          </Position>
        )}

        <Copy>
          {access?.tgId
            ? t('access.waitlist.telegram_note')
            : t('access.waitlist.come_back_note')}
        </Copy>

        {shareLink ? (
          <Share>
            <ShareLabel>
              {t('access.waitlist.share', { count: access?.brought ?? 0 })}
            </ShareLabel>
            <ShareRow>
              <ShareLink>{shareLink}</ShareLink>
              <CopyButton value={shareLink} />
            </ShareRow>
          </Share>
        ) : undefined}

        <Actions>
          <Button
            variant="glass-2"
            size="s"
            radius="rounded"
            onClick={() => refresh()}
          >
            {t('access.waitlist.check_again')}
          </Button>
          <SignOut type="button" onClick={() => logout()}>
            {t('access.waitlist.sign_out')}
          </SignOut>
        </Actions>
      </Card>
    </Root>
  )
}

const Root = styled.main`
  min-height: 100dvh;
  display: grid;
  place-items: center;
  padding: 24px 16px;
  background: var(--background-1);
`

const Card = styled.section`
  width: min(420px, 100%);
  display: grid;
  justify-items: center;
  gap: 14px;
  text-align: center;
`

const Brand = styled.span`
  color: var(--text-12);
  font-size: 15px;
  font-weight: 700;
`

const Title = styled.h1`
  margin: 8px 0 0;
  color: var(--text-12);
  font-size: 24px;
  font-weight: 700;
`

const Position = styled.div`
  display: grid;
  gap: 2px;
  padding: 14px 28px;
  border-radius: 14px;
  background: var(--background-3);
`

const PositionLabel = styled.span`
  color: var(--text-11);
  font-size: 12px;
  font-weight: 500;
`

const PositionValue = styled.span`
  color: var(--primary-9);
  font-size: 32px;
  font-weight: 800;
`

const Copy = styled.p`
  margin: 0;
  color: var(--text-11);
  font-size: 13px;
  line-height: 1.5;
`

const Share = styled.div`
  width: 100%;
  display: grid;
  gap: 6px;
  padding: 12px;
  border-radius: 12px;
  background: var(--background-2);
  text-align: start;
`

const ShareLabel = styled.span`
  color: var(--text-11);
  font-size: 12px;
  font-weight: 500;
`

const ShareRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
`

const ShareLink = styled.span`
  color: var(--text-12);
  font-size: 12px;
  word-break: break-all;
`

const Actions = styled.div`
  display: grid;
  justify-items: center;
  gap: 10px;
  margin-top: 6px;
`

const SignOut = styled.button`
  border: none;
  background: transparent;
  color: var(--text-11);
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: var(--text-12);
  }
`
